import React from 'react';
import { Usb, Wifi, WifiOff, CreditCard, Loader2, AlertCircle, Check } from 'lucide-react';
import { useRfidBridge } from '@/shared/hooks';
import { Button } from '../atoms/Button';
import type { BridgeStatus } from '@/shared/services/RfidBridgeService';

interface RfidReaderPanelProps {
  /** Callback cuando se lee una tarjeta */
  onCardRead: (uid: string) => void;
  /** Clase CSS adicional */
  className?: string;
}

const getStatusConfig = (status: BridgeStatus) => {
  switch (status) {
    case 'disconnected':
      return {
        label: 'Desconectado',
        color: 'text-slate-500',
        bgColor: 'bg-slate-100',
        borderColor: 'border-slate-200',
        icon: WifiOff,
      };
    case 'connecting':
      return {
        label: 'Conectando...',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        icon: Loader2,
      };
    case 'connected':
      return {
        label: 'Conectado',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: Wifi,
      };
    case 'reader_ready':
      return {
        label: 'Lectora Lista',
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        icon: CreditCard,
      };
    case 'card_present':
      return {
        label: 'Tarjeta Detectada',
        color: 'text-primary-600',
        bgColor: 'bg-primary-50',
        borderColor: 'border-primary-200',
        icon: Check,
      };
    case 'error':
      return {
        label: 'Error',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: AlertCircle,
      };
    default:
      return {
        label: status,
        color: 'text-slate-500',
        bgColor: 'bg-slate-100',
        borderColor: 'border-slate-200',
        icon: WifiOff,
      };
  }
};

/**
 * Panel para conectar con lectora RFID y capturar códigos de tarjeta.
 *
 * Requiere que el RFID Bridge (app de escritorio) esté ejecutándose.
 */
export const RfidReaderPanel: React.FC<RfidReaderPanelProps> = ({
  onCardRead,
  className = '',
}) => {
  const { status, cardUid, readerName, error, connect, disconnect, hasCard } = useRfidBridge();

  const config = getStatusConfig(status);
  const StatusIcon = config.icon;

  return (
    <div className={`rounded-xl border ${config.borderColor} ${config.bgColor} p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-slate-700 flex items-center gap-2">
          <Usb size={18} />
          Lectora RFID
        </h4>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
          <StatusIcon size={12} className={status === 'connecting' ? 'animate-spin' : ''} />
          {config.label}
        </span>
      </div>

      {/* Estado: Desconectado */}
      {status === 'disconnected' && (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            Conecte la lectora USB y ejecute el RFID Bridge para leer tarjetas automáticamente.
          </p>
          <Button onClick={connect} size="sm" variant="secondary" icon={<Wifi size={14} />}>
            Conectar al Bridge
          </Button>
        </div>
      )}

      {/* Estado: Conectando */}
      {status === 'connecting' && (
        <div className="flex items-center gap-2 text-sm text-blue-600">
          <Loader2 size={16} className="animate-spin" />
          Conectando al RFID Bridge...
        </div>
      )}

      {/* Estado: Error */}
      {status === 'error' && (
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-sm text-red-600">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error || 'No se pudo conectar al RFID Bridge'}</span>
          </div>
          <div className="text-xs text-slate-500 bg-white/50 rounded-lg p-2">
            <strong>Asegúrese de:</strong>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              <li>El RFID Bridge está ejecutándose</li>
              <li>La lectora USB está conectada</li>
              <li>No hay firewall bloqueando el puerto 8765</li>
            </ul>
          </div>
          <Button onClick={connect} size="sm" variant="secondary">
            Reintentar
          </Button>
        </div>
      )}

      {/* Estado: Conectado (sin lectora) */}
      {status === 'connected' && (
        <div className="space-y-2">
          <p className="text-sm text-green-600">
            Conectado al bridge. Esperando lectora...
          </p>
          <p className="text-xs text-slate-500">
            Conecte una lectora RFID USB al equipo.
          </p>
        </div>
      )}

      {/* Estado: Lectora lista */}
      {status === 'reader_ready' && (
        <div className="space-y-2">
          {readerName && (
            <p className="text-xs text-slate-500 font-mono truncate">
              {readerName}
            </p>
          )}
          <div className="flex items-center gap-2 text-sm text-emerald-600 animate-pulse">
            <CreditCard size={16} />
            Acerque una tarjeta a la lectora...
          </div>
        </div>
      )}

      {/* Estado: Tarjeta detectada */}
      {status === 'card_present' && hasCard && cardUid && (
        <div className="space-y-3">
          <div className="bg-white rounded-lg p-3 border border-primary-200">
            <p className="text-xs text-slate-500 mb-1">Código de tarjeta:</p>
            <p className="font-mono text-lg font-bold text-slate-800 tracking-wider">
              {cardUid}
            </p>
          </div>
          <Button
            onClick={() => onCardRead(cardUid)}
            variant="primary"
            size="sm"
            className="w-full"
            icon={<Check size={14} />}
          >
            Usar este código
          </Button>
        </div>
      )}

      {/* Botón de desconectar (cuando está conectado) */}
      {(status === 'connected' || status === 'reader_ready' || status === 'card_present') && (
        <div className="mt-3 pt-3 border-t border-slate-200/50">
          <button
            onClick={disconnect}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Desconectar
          </button>
        </div>
      )}
    </div>
  );
};
